import React, { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useCompanyPlans, useSystems, useSystemPlans, useApplyPlan } from "@/hooks/use-plans";
import { useInvoiceCalculations } from "@/hooks/useInvoiceCalculations";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Crown, Zap, Star, Check, X, Settings, Radio } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { getSystemIcon } from "@/lib/system-icons";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { FEATURE_NAMES } from "@/lib/plans/enforce";
import { Plan } from "@/lib/plans/types";
import { EnhancedPlanCard } from "@/components/admin/EnhancedPlanCard";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ProductsSelection } from "@/components/company/ProductsSelection";

export default function CompanyPlansPage() {
  const { activeCompany } = useAuth();
  const company = activeCompany;
  const { data: allSystems = [] } = useSystems();
  const { data: currentPlans = [] } = useCompanyPlans(company?.id);
  const { calculateNextBillingDate } = useInvoiceCalculations();
  
  // Filter active systems only
  const systems = allSystems.filter(system => (system as any).is_active !== false);
  
  // Check if TrackingONE is active to show GPS products
  const hasActiveTrackingONE = systems.some(system => 
    system.code === 'gps_tracking' && (system as any).is_active !== false
  );
  
  console.log('🔍 Current Plans Debug:', currentPlans);
  const [selectedSystem, setSelectedSystem] = useState("");

  useEffect(() => {
    if (systems.length > 0 && !selectedSystem) {
      setSelectedSystem(systems[0]?.code || "");
    }
  }, [systems, selectedSystem]);

  if (!company) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Empresa não encontrada
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Planos e Assinaturas</h1>
          <p className="text-muted-foreground">
            Gerencie os planos ativos da empresa e configure novos sistemas
          </p>
        </div>
      </div>

      {/* Seção de Planos Ativos */}
      {Array.isArray(currentPlans) && currentPlans.length > 0 && (
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <div className="w-1 h-6 bg-gradient-primary rounded-full" />
            <h2 className="text-xl font-semibold">Planos Ativos</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {currentPlans.map((currentPlan) => (
              <Card key={currentPlan.id} className="border-0 shadow-card hover:shadow-card-hover transition-all duration-300">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="inline-flex p-2 rounded-lg bg-gradient-to-br from-primary to-primary/80">
                        {React.createElement(getSystemIcon(currentPlan?.system?.code), { 
                          className: "h-4 w-4 text-white" 
                        })}
                      </div>
                      <div>
                        <CardTitle className="text-base">{currentPlan?.system?.name}</CardTitle>
                        <p className="text-sm text-muted-foreground">{currentPlan.plan?.name || 'N/A'}</p>
                      </div>
                    </div>
                    <Badge variant="default" className="bg-success text-success-foreground">
                      Ativo
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-primary">
                      {formatCurrency(
                        currentPlan.billing_period === 'yearly' 
                          ? (currentPlan.plan?.price_yearly || 0)
                          : (currentPlan.plan?.price_monthly || 0)
                      )}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      /{currentPlan.billing_period === 'yearly' ? 'ano' : 'mês'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Próxima cobrança:</span>
                    <span className="font-medium">{calculateNextBillingDate()}</span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => {
                        setSelectedSystem(currentPlan?.system?.code);
                        // Scroll para as abas
                        setTimeout(() => {
                          document.querySelector('[role="tablist"]')?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                    >
                      Alterar Plano
                    </Button>
                    <CancelPlanButton 
                      planId={currentPlan.id}
                      planName={currentPlan.plan?.name || 'N/A'}
                      systemName={currentPlan.system?.name || 'N/A'}
                      companyId={company?.id || ""}
                      systemCode={currentPlan?.system?.code || ''}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {systems.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Settings className="h-12 w-12 mx-auto text-muted-foreground" />
              <div>
                <h3 className="text-lg font-semibold">Nenhum Sistema Disponível</h3>
                <p className="text-muted-foreground">
                  Entre em contato com o suporte para configurar sistemas
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <div className="w-1 h-6 bg-gradient-primary rounded-full" />
            <h2 className="text-xl font-semibold">Sistemas Disponíveis</h2>
            <span className="text-sm text-muted-foreground">Selecione um sistema para ver os planos disponíveis</span>
          </div>
          <Tabs value={selectedSystem} onValueChange={setSelectedSystem}>
            <div className="flex justify-center w-full">
              <TabsList className="inline-flex w-auto bg-card">
              {systems.map((system) => {
                const IconComponent = getSystemIcon(system.code);
                return (
                  <TabsTrigger 
                    key={system.code} 
                    value={system.code}
                    className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    <IconComponent className="h-4 w-4 mr-2" />
                    {system.name}
                  </TabsTrigger>
                );
              })}
              {hasActiveTrackingONE && (
                <TabsTrigger 
                  value="gps-trackers"
                  className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  <Radio className="h-4 w-4 mr-2" />
                  Rastreadores GPS
                </TabsTrigger>
              )}
              </TabsList>
            </div>

            {systems.map((system) => (
              <TabsContent key={system.code} value={system.code} className="space-y-6 mt-6">
                <SystemPlansSelection 
                  systemCode={system.code} 
                  currentPlans={currentPlans}
                />
              </TabsContent>
            ))}

            {hasActiveTrackingONE && (
              <TabsContent value="gps-trackers" className="space-y-6 mt-6">
                <ProductsSelection productType="gps_tracker" />
              </TabsContent>
            )}
          </Tabs>
        </div>
      )}
    </div>
  );
}

function CancelPlanButton({
  planId,
  planName,
  systemName,
  companyId,
  systemCode
}: {
  planId: string;
  planName: string;
  systemName: string;
  companyId: string;
  systemCode: string;
}) {
  const applyPlan = useApplyPlan();

  const handleCancel = () => {
    applyPlan.mutate({
      companyId,
      systemCode,
      planId,
      action: 'cancel',
      reason: `Usuário cancelou plano ${planName} do sistema ${systemName}`
    });
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button 
          variant="destructive" 
          size="sm"
          disabled={applyPlan.isPending}
        >
          <X className="h-3 w-3" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancelar Plano</AlertDialogTitle>
          <AlertDialogDescription>
            Tem certeza que deseja cancelar o plano <strong>{planName}</strong> do sistema <strong>{systemName}</strong>?
            <br /><br />
            <span className="text-destructive font-medium">
              ⚠️ Esta ação irá suspender o acesso ao sistema imediatamente.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleCancel}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {applyPlan.isPending ? "Cancelando..." : "Confirmar Cancelamento"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function SystemPlansSelection({ 
  systemCode, 
  currentPlans 
}: { 
  systemCode: string; 
  currentPlans: any[];
}) {
  const { data: plans = [], isLoading } = useSystemPlans(systemCode);
  const { activeCompany } = useAuth();
  const applyPlan = useApplyPlan();
  const currentPlan = Array.isArray(currentPlans) 
    ? currentPlans.find(cp => cp?.system?.code === systemCode)
    : undefined;

  if (isLoading) {
    return <div>Carregando planos...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {plans.map((plan) => (
          <EnhancedPlanCard 
            key={plan.id} 
            plan={plan}
            systemCode={systemCode}
            currentPlan={currentPlan}
            onSelect={(billingPeriod) => {
              applyPlan.mutate({
                companyId: activeCompany?.id || "",
                systemCode: systemCode,
                planId: plan.id,
                action: currentPlan ? 'upgrade' : 'activate',
                billingPeriod,
                reason: `Usuário ${currentPlan ? 'alterou para' : 'selecionou'} plano ${plan.name}`
              });
            }}
            disabled={applyPlan.isPending}
          />
        ))}
      </div>
    </div>
  );
}

function PlanCard({ 
  plan, 
  currentPlan
}: { 
  plan: Plan; 
  currentPlan?: any;
}) {
  const { activeCompany } = useAuth();
  const isCurrentPlan = currentPlan?.plan_id === plan.id;
  const hasActivePlan = !!currentPlan;

  const getPlanIcon = (planName: string) => {
    const name = planName.toLowerCase();
    if (name.includes('basic')) return <Star className="h-5 w-5" />;
    if (name.includes('pro')) return <Zap className="h-5 w-5" />;
    if (name.includes('enterprise')) return <Crown className="h-5 w-5" />;
    return <Star className="h-5 w-5" />;
  };

  return (
    <Card className={`relative flex flex-col h-full ${isCurrentPlan ? 'ring-2 ring-primary' : ''}`}>
      {isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <Badge className="bg-primary text-primary-foreground">Plano Atual</Badge>
        </div>
      )}

      <CardHeader className="text-center flex-shrink-0">
        <div className="flex justify-center mb-2">
          {getPlanIcon(plan.name)}
        </div>
        <CardTitle className="text-xl">{plan.name}</CardTitle>
        <CardDescription>{plan.description}</CardDescription>
        
        <div className="py-4">
          <div className="text-3xl font-bold">
            {formatCurrency(plan.price_monthly)}
            <span className="text-sm font-normal text-muted-foreground">/mês</span>
          </div>
          <div className="text-sm text-muted-foreground">
            ou {formatCurrency(plan.price_yearly)}/ano
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col">
        <div className="flex-1 space-y-3 min-h-[120px]">
          <h4 className="text-sm font-semibold">Recursos inclusos:</h4>
          <div className="space-y-2">
            {Object.entries(plan.features.limits).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span>
                  {value === 999999 ? "Ilimitado" : value.toLocaleString()} {" "}
                  {FEATURE_NAMES[key as keyof typeof FEATURE_NAMES] || key}
                </span>
              </div>
            ))}
            
            {Object.entries(plan.features.toggles)
              .filter(([, enabled]) => enabled)
              .map(([key]) => (
                <div key={key} className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span>{FEATURE_NAMES[key as keyof typeof FEATURE_NAMES] || key}</span>
                </div>
              ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
